import { useEffect, useMemo, useRef, useState } from 'react'
import { enterAssistedMode } from '../auth/authSource'
import { AssistError, exchangeAssistToken } from '../data/assist'
import { assistErrorCopy } from './AssistLanding'
import { AuthFlowError, signIn } from '../auth/cognitoAuth'
import { clearSession, loadSession, storeSession } from '../auth/session'
import {
  claimInvite,
  createClaimAccount,
  guardianClaimContext,
  lookupClaim,
  myApiBase,
  MyApiError,
  type ClaimLookup,
  type ClaimResult,
} from '../data/myApi'
import { recordGuardianClaim, removeGuardianClaim } from '../data/guardianClaim'
import { useLocale } from '../i18n/useLocale'
import { Brand, Button, Card, StatusBadge } from '../components/ui'

/*
 * /claim/<inviteToken> — the invite-email landing (OnePulse slice 1, reusing
 * the PR #4 claim-flow intent: this IS the C1/C2 claim path, now that its
 * backend exists in services/my-api).
 *
 * Signed out: show who invited you to what, then hosted-UI sign-in/sign-up
 * (the token is stashed for the OAuth round trip). Signed in: claim via the
 * API — authorisation is the VERIFIED EMAIL MATCH server-side; the URL token
 * is only a lookup key, so a forwarded link cannot take the ticket.
 *
 * GUARDIAN VARIANT (S2 slice, BOARDROOM-2026-09-03-052111 / CMT-2026-09-03-
 * 020): when GET /claim returns claim_kind='guardian' the landing leads with
 * the CHILD's context — "{child}, your child, has a ticket for {event}" — and
 * frames the CTA as claiming/managing the seat AS parent/guardian ("you'll
 * sign their waiver and get their race QR"). Duncan's live-walk defect: this
 * email CTA previously landed the parent in the STANDARD self-claim flow. On
 * a successful guardian claim the seat is recorded in the device-local
 * guardian-claim store (src/data/guardianClaim.ts) — the carrier for the
 * guardian get-ready journey (the wire states guardianship only here). Absent
 * claim_kind renders byte-identical to today's self-claim.
 */

type Phase =
  | { kind: 'loading' }
  | { kind: 'unconfigured' }
  | { kind: 'lookup_failed'; status: number }
  | { kind: 'landing'; lookup: ClaimLookup }
  | { kind: 'claiming'; lookup: ClaimLookup }
  | { kind: 'claimed'; result: ClaimResult; guardian: { childName: string } | null }
  | { kind: 'claim_failed'; lookup: ClaimLookup; code: string }

/** The consumer pool's policy for a NEW account (my-api CONSUMER_PASSWORD_POLICY;
 *  infra cognito.tf). Shown before submit; an existing account's password is
 *  never judged, so the field is not blocked on it. */
export const PASSWORD_RULES_HINT =
  'New account? Use at least 10 characters, with an uppercase letter, a lowercase letter and a number.'

const RULE_COPY: Record<string, string> = {
  min_length: 'at least 10 characters',
  uppercase: 'an uppercase letter',
  lowercase: 'a lowercase letter',
  number: 'a number',
}

/** The invited email ALREADY has an account (create-account 200 'exists')
 *  and the typed password is not that account's password (prod 2026-10-31: an
 *  Apex invitee typed a NEW password and got the generic "That didn't work").
 *  Says so plainly and offers the email-code sign-in that returns here. */
export const EXISTING_ACCOUNT_COPY =
  'This email already has a My Trumin account, so this isn’t a new password — enter that account’s password. Forgotten it? Use “Email me a sign-in code” instead.'

/** Login, coming back to this claim once signed in (returnTo). A help-desk
 *  claim-mode link keeps its `?assist=` token across the sign-in detour. */
export function claimSignInHref(token: string, assist: string | null = null): string {
  return `/?returnTo=${encodeURIComponent(`/claim/${token}${assist ? `?assist=${assist}` : ''}`)}`
}

/** The 400 password_policy copy: exactly the rules the password missed. */
export function passwordPolicyCopy(rules: unknown): string {
  const missed = Array.isArray(rules) ? rules.map((r) => RULE_COPY[String(r)]).filter(Boolean) : []
  if (missed.length === 0) return `That password isn’t accepted for a new account. ${PASSWORD_RULES_HINT}`
  const list = missed.length === 1 ? missed[0] : `${missed.slice(0, -1).join(', ')} and ${missed[missed.length - 1]}`
  return `Your new password needs ${list}.`
}

/** Where a successful claim lands: the claimed event's Get ready view
 *  (GetReady's ?event= focus, ruling c on PR #311). */
export function getReadyHref(eventId: string | null | undefined): string {
  return eventId ? `/get-ready?event=${encodeURIComponent(eventId)}` : '/get-ready'
}

/** Pause on TICKET CLAIMED before moving on (prod readiness push 2026-10-29:
 *  people stayed on /claim and never reached the fitness step). */
export const CLAIMED_REDIRECT_MS = 3000

/** HELP-DESK CLAIM MODE (LOCKED contract addendum 13:00Z): the help desk's
 *  link for an UNCLAIMED place is this claim link + `?assist=<token>`. The
 *  token stays in the URL (so the in-page sign-in reload keeps it) and is
 *  exchanged only AFTER the participant's own email-code claim succeeds. */
export function assistParamFromUrl(search: string = typeof window === 'undefined' ? '' : window.location.search): string | null {
  const v = new URLSearchParams(search).get('assist')
  return v && /^[A-Za-z0-9_-]{43}$/.test(v) ? v : null
}

export function ClaimLanding({ token }: { token: string }) {
  const session = useMemo(() => loadSession(), [])
  const assistToken = useMemo(() => assistParamFromUrl(), [])
  const [assistError, setAssistError] = useState<string | null>(null)
  const assistFired = useRef(false)
  const { t } = useLocale()
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' })
  const [busy, setBusy] = useState(false)
  const [password, setPassword] = useState('')
  const [authErr, setAuthErr] = useState<string | null>(null)
  const [existingAccount, setExistingAccount] = useState(false)
  // Existing account: the password field is the SECONDARY route, shown on
  // "Use my password instead" (or after an existing-password attempt failed).
  const [showPassword, setShowPassword] = useState(false)
  const claimFired = useRef(false)

  // TICKET CLAIMED → Get ready automatically after a short pause (the button
  // goes at once). Cleared on unmount.
  const claimedEventId = phase.kind === 'claimed' ? phase.result.eventId : undefined
  useEffect(() => {
    if (phase.kind !== 'claimed') return
    // Help-desk claim mode: the assisted session takes over (below) — no
    // redirect into the participant's own Get Ready.
    if (assistToken) return
    const timer = setTimeout(() => window.location.replace(getReadyHref(claimedEventId)), CLAIMED_REDIRECT_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, claimedEventId])

  useEffect(() => {
    const base = myApiBase()
    if (!base) {
      setPhase({ kind: 'unconfigured' })
      return
    }
    let cancelled = false
    lookupClaim(base, token)
      .then((lookup) => {
        if (cancelled) return
        setPhase({ kind: 'landing', lookup })
      })
      .catch((e) => {
        if (cancelled) return
        setPhase({ kind: 'lookup_failed', status: e instanceof MyApiError ? e.status : 0 })
      })
    return () => {
      cancelled = true
    }
  }, [token])

  // HELP-DESK CLAIM MODE: once THIS participant's own claim succeeded, exchange
  // the single-use assist token, drop their Cognito session from the shared
  // tablet's real storage, and continue in the assisted Get Ready (memory-only
  // session; the witness record and the completion hook apply).
  useEffect(() => {
    if (phase.kind !== 'claimed' || !assistToken || assistFired.current) return
    const base = myApiBase()
    if (!base) return
    assistFired.current = true
    void (async () => {
      try {
        const s = await exchangeAssistToken(base, assistToken)
        clearSession()
        if (!enterAssistedMode(s)) {
          setAssistError('storage')
          return
        }
        // In-app move (no reload: the session is memory-only). The router
        // follows popstate; the claim URL (with its used token) is replaced.
        window.history.replaceState(null, '', '/get-ready')
        window.dispatchEvent(new PopStateEvent('popstate'))
      } catch (e) {
        setAssistError(e instanceof AssistError ? e.code : 'unknown')
      }
    })()
  }, [phase.kind, assistToken])

  // Signed in + invite still open -> claim exactly once.
  useEffect(() => {
    const base = myApiBase()
    if (!base || !session || claimFired.current) return
    if (phase.kind !== 'landing' || phase.lookup.state !== 'invited') return
    claimFired.current = true
    setPhase({ kind: 'claiming', lookup: phase.lookup })
    const lookupGuardian = guardianClaimContext(phase.lookup)
    claimInvite(session.idToken, base, token)
      .then((result) => {
        // GUARDIAN CLAIM (CMT-2026-09-03-020): persist the guardian nature of
        // this seat NOW — the claim wires are the only reads that state it.
        // The POST result's own additive carry (claimKind/childName, S1
        // camelCase contract) is AUTHORITATIVE when present in EITHER
        // direction (adversarial review P3): an explicit 'self' OVERRIDES a
        // guardian lookup (the row changed between lookup and claim — the
        // POST is the later, transactional truth), 'guardian' claims it. Only
        // an ABSENT claimKind (older my-api) falls back to the GET lookup's
        // context. The record drives the guardian get-ready journey
        // (child-named card, child-confirm step, guardian waiver framing).
        // Best-effort: a storage failure never blocks the claim itself.
        const guardian = result.claimKind === 'guardian'
          ? { childName: (result.childName ?? '').trim() || lookupGuardian?.childName || '' }
          : result.claimKind === 'self'
            ? null
            : lookupGuardian
        try {
          if (guardian) {
            recordGuardianClaim({ ticketId: result.ticketId, eventId: result.eventId, childName: guardian.childName })
          } else {
            // Adversarial review P2: a NON-guardian claim of this ticket
            // purges any stale guardian record (revoke → re-invite →
            // self-claim) — otherwise the now-self seat would keep the
            // child-named card, the childConfirm step and the suppressed
            // eligibility gate.
            removeGuardianClaim(result.ticketId)
          }
        } catch { /* device-local record only — the claim stands */ }
        setPhase({ kind: 'claimed', result, guardian })
      })
      .catch((e) =>
        setPhase({
          kind: 'claim_failed',
          lookup: phase.lookup,
          code: e instanceof MyApiError && e.code ? e.code : 'unknown',
        }),
      )
  }, [phase, session, token])

  // Single smart-path (BOARDROOM-222954): NO self-declared create-vs-signin. The
  // server (my-api, Cognito-admin) detects whether the invited email already has
  // an account and either CREATES it with this password (new invitee) or reports
  // it exists; either way we then SRP sign-in with the entered password and the
  // claim effect below fires. A new invitee never lands on a failing sign-in.
  const onSetPassword = async () => {
    const base = myApiBase()
    if (!base) return
    if (password.length < 8) { setAuthErr('Choose a password of at least 8 characters.'); return }
    setBusy(true)
    setAuthErr(null)
    let outcome: 'created' | 'exists' | null = null
    try {
      const created = await createClaimAccount(base, token, password)
      outcome = created.outcome
      const email = created.email
      const result = await signIn(email, password)
      if (result.status !== 'ok') {
        setAuthErr('Unexpected sign-in step — please try again.')
        setBusy(false)
        return
      }
      storeSession(result.tokens)
      // Remount with a session → the claim effect claims the ticket + routes to
      // get-ready (mirrors the branded-login DONE = storeSession + reload).
      window.location.reload()
    } catch (e) {
      // A NEW account's password that misses the pool policy (my-api 400
      // password_policy — prod 2026-10-29, was a raw 500): say exactly which
      // rules it missed. Otherwise a create/network failure, OR (existing
      // account) a wrong password.
      if (e instanceof MyApiError && e.status === 400 && e.code === 'password_policy') {
        setAuthErr(passwordPolicyCopy(e.body?.rules))
      } else if (outcome === 'exists' && e instanceof AuthFlowError && (e.code === 'NotAuthorized' || e.code === 'UserNotFound')) {
        // The account exists and this is not its password: never the generic
        // "try again shortly" (that reads as an outage).
        setExistingAccount(true)
        setShowPassword(true)
        setAuthErr(EXISTING_ACCOUNT_COPY)
      } else {
        setAuthErr(
          'That didn’t work. If you already have an account for this invite, enter your existing password. Otherwise try again shortly.',
        )
      }
      setBusy(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
      <div className="col" style={{ width: 400, gap: 18 }}>
        <div className="col" style={{ alignItems: 'center', gap: 10 }}>
          <Brand size={30} />
          <h1 style={{ fontSize: 20 }}>My Trumin</h1>
        </div>

        {phase.kind === 'loading' && (
          <p className="secondary" role="status" style={{ fontSize: 13, textAlign: 'center' }}>Looking up your invite…</p>
        )}

        {phase.kind === 'unconfigured' && (
          <p className="faint" data-wired="false" role="status" style={{ fontSize: 12, textAlign: 'center', color: 'var(--warn)' }}>
            The claim service is not connected in this build — your invite is safe;
            claiming works the moment the service is live. Nothing here is fabricated.
          </p>
        )}

        {phase.kind === 'lookup_failed' && (
          <p role="alert" className="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
            {phase.status === 404
              ? 'This invite link is not recognised — it may be incomplete (some email apps cut long links) or it was re-sent. Sign in at my.trumin.com with the email address your ticket was sent to: your ticket is waiting in Get ready.'
              : 'The claim service is not responding right now — try this link again shortly.'}
          </p>
        )}

        {(phase.kind === 'landing' || phase.kind === 'claiming' || phase.kind === 'claim_failed') && (
          <InviteCard lookup={phase.kind === 'landing' ? phase.lookup : phase.kind === 'claiming' ? phase.lookup : phase.lookup} />
        )}

        {phase.kind === 'landing' && phase.lookup.state !== 'invited' && (
          <p className="secondary" role="status" style={{ fontSize: 13, textAlign: 'center' }}>
            {phase.lookup.state === 'revoked'
              ? 'This invite was revoked by the purchaser — ask them to re-invite you if that is unexpected.'
              : 'This ticket has already been claimed. Sign in with the invited email to see it.'}
          </p>
        )}

        {phase.kind === 'landing' && phase.lookup.state === 'invited' && !session && (() => {
          // EXISTING ACCOUNT (boardroom-365 Tier B 2026-10-31, quality G18): the
          // invited email already has an account — known up front (GET /claim
          // accountExists) or learned from create-account 'exists'. Email code is
          // the participant's PRIMARY sign-in (PROGRAMME-DESIGN-AUTH-POPULATION-
          // MODEL-V1), so it leads; the existing password is the secondary route.
          // Login returns here (returnTo) and the claim effect then claims.
          const existing = phase.lookup.accountExists === true || existingAccount
          const passwordField = (
            <input
              data-testid="claim-password"
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (authErr) setAuthErr(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter') void onSetPassword() }}
              placeholder="Password"
              autoComplete="current-password"
              aria-label="Password"
              disabled={busy}
              style={{
                width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 15,
                borderRadius: 10, border: '1px solid var(--hairline-16)', background: 'var(--surface)',
                color: 'var(--text)', fontFamily: 'inherit',
              }}
            />
          )
          const errorLine = authErr && (
            <p data-testid="claim-auth-error" role="alert" className="secondary" style={{ fontSize: 12, color: 'var(--warn)', textAlign: 'center' }}>
              {authErr}
            </p>
          )
          if (existing) {
            return (
              <div className="col" data-testid="claim-existing-account" style={{ gap: 10 }}>
                <p className="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                  You already have a My Trumin account for <strong>{phase.lookup.allocatedEmailMasked}</strong>. Sign in to claim your ticket.
                </p>
                <Button variant="primary" full onClick={() => window.location.assign(claimSignInHref(token, assistToken))}>
                  Email me a sign-in code
                </Button>
                {showPassword ? (
                  <>
                    {passwordField}
                    {errorLine}
                    <Button variant="outline" full onClick={() => void onSetPassword()} disabled={busy || password.length < 8}>
                      {busy ? 'Signing in…' : 'Sign in with my password'}
                    </Button>
                  </>
                ) : (
                  <button
                    type="button"
                    data-testid="claim-use-password"
                    onClick={() => setShowPassword(true)}
                    style={{
                      alignSelf: 'center', border: 0, background: 'transparent', padding: '6px 0',
                      fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)',
                      textDecoration: 'underline', cursor: 'pointer',
                    }}
                  >
                    Use my password instead
                  </button>
                )}
                <p className="faint" style={{ fontSize: 11, textAlign: 'center' }}>
                  Use the email your ticket was sent to. You’ll come straight back here to finish claiming.
                </p>
              </div>
            )
          }
          return (
            <div className="col" style={{ gap: 10 }}>
              <p className="secondary" style={{ fontSize: 13, textAlign: 'center' }}>
                Set a password for <strong>{phase.lookup.allocatedEmailMasked}</strong> to claim your ticket and get ready.
              </p>
              {passwordField}
              <p data-testid="claim-password-rules" className="faint" style={{ fontSize: 11, textAlign: 'center', margin: 0 }}>
                {PASSWORD_RULES_HINT}
              </p>
              {errorLine}
              <Button variant="primary" full onClick={() => void onSetPassword()} disabled={busy || password.length < 8}>
                {busy ? 'Setting up…' : 'Set password & get ready'}
              </Button>
              <p className="faint" style={{ fontSize: 11, textAlign: 'center' }}>
                New here? This creates your account. Already have one? Enter your existing password.
                Your ticket is tied to this email.
              </p>
            </div>
          )
        })()}

        {phase.kind === 'claiming' && (
          <p className="secondary" role="status" style={{ fontSize: 13, textAlign: 'center' }}>Claiming your ticket…</p>
        )}

        {phase.kind === 'claimed' && (
          <div className="col" style={{ gap: 12, textAlign: 'center' }}>
            <StatusBadge tone="success" text="TICKET CLAIMED" />
            {/* Guardian claim (CMT-2026-09-03-020): the success copy stays in
                the child's voice — the seat is the CHILD's, managed by the
                parent. Self-claim copy is byte-identical to before. */}
            {phase.guardian ? (
              <p data-testid="claim-guardian-claimed" className="secondary" style={{ fontSize: 13 }}>
                {phase.guardian.childName
                  ? t('claim.guardian.claimed', {
                      child: phase.guardian.childName,
                      type: phase.result.ticketTypeName,
                      event: phase.result.eventName,
                    })
                  : t('claim.guardian.claimedNoName', {
                      type: phase.result.ticketTypeName,
                      event: phase.result.eventName,
                    })}
              </p>
            ) : (
              <p className="secondary" style={{ fontSize: 13 }}>
                {phase.result.ticketTypeName} · {phase.result.eventName} is yours.
                Next: get ready — complete your profile, waiver and heat before your
                entry QR can be issued.
              </p>
            )}
            {assistToken ? (
              assistError ? (
                <p role="alert" data-testid="assist-error" className="secondary" style={{ fontSize: 13 }}>{assistErrorCopy(assistError)}</p>
              ) : (
                <p className="faint" role="status" style={{ fontSize: 11 }}>Opening your Get ready with the help desk…</p>
              )
            ) : (
              <>
                <Button variant="primary" full onClick={() => window.location.replace(getReadyHref(phase.result.eventId))}>
                  Continue to Get ready
                </Button>
                <p className="faint" role="status" style={{ fontSize: 11 }}>Taking you to Get ready…</p>
              </>
            )}
          </div>
        )}

        {phase.kind === 'claim_failed' && (
          <div className="col" style={{ gap: 10, textAlign: 'center' }}>
            <p role="alert" className="secondary" style={{ fontSize: 13 }}>
              {phase.code === 'email_mismatch'
                ? `This invite belongs to ${phase.lookup.allocatedEmailMasked}, which doesn't match the account you're signed in with.`
                : phase.code === 'not_claimable'
                  ? 'This invite is no longer open — it may already be claimed or revoked.'
                  : 'Claiming failed — the service returned an error. Try again shortly.'}
            </p>
            {phase.code === 'email_mismatch' && (
              <Button variant="outline" full onClick={() => { clearSession(); window.location.reload() }}>
                Switch account
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function InviteCard({ lookup }: { lookup: ClaimLookup }) {
  const { t } = useLocale()
  // GUARDIAN variant (BOARDROOM-2026-09-03-052111 / CMT-2026-09-03-020): lead
  // with the CHILD's context and frame the ask as parent/guardian management —
  // never the self-claim "allocated you a ticket" voice. claim_kind absent ⇒
  // the self-claim card below, byte-identical to before this slice.
  const guardian = guardianClaimContext(lookup)
  if (guardian) {
    return (
      <Card>
        <div className="col" data-testid="claim-guardian-card" style={{ padding: 18, gap: 6, textAlign: 'center' }}>
          <span className="eyebrow">{t('claim.guardian.eyebrow')}</span>
          <h2 style={{ fontSize: 18 }}>{lookup.eventName}</h2>
          <span className="secondary" style={{ fontSize: 12 }}>
            {lookup.ticketTypeName}
            {lookup.venue ? ` · ${lookup.venue}` : ''}
          </span>
          <p data-testid="claim-guardian-lead" className="secondary" style={{ fontSize: 13, marginTop: 6 }}>
            {guardian.childName
              ? t('claim.guardian.lead', { child: guardian.childName, event: lookup.eventName })
              : t('claim.guardian.leadNoName', { event: lookup.eventName })}{' '}
            {t('claim.guardian.frame')}
          </p>
        </div>
      </Card>
    )
  }
  return (
    <Card>
      <div className="col" style={{ padding: 18, gap: 6, textAlign: 'center' }}>
        <span className="eyebrow">You&rsquo;re invited</span>
        <h2 style={{ fontSize: 18 }}>{lookup.eventName}</h2>
        <span className="secondary" style={{ fontSize: 12 }}>
          {lookup.ticketTypeName}
          {lookup.venue ? ` · ${lookup.venue}` : ''}
        </span>
        <p className="secondary" style={{ fontSize: 13, marginTop: 6 }}>
          {lookup.invitedByName ?? 'Your purchaser'} allocated you a ticket —
          claim it and get ready.
        </p>
      </div>
    </Card>
  )
}
